<template>
  <div>
    <!-- step 1 and 2 -->
    <div class="" aria-live="polite">
      <h5 ref="sectionHeader" class="demi caps-quarter" tabindex="-1">
        {{ sectionTitle }}
      </h5>
    </div>

    <!-- step 1 - billing -->
    <div v-show="step === 'billing'" class="update-payment-address">
      <billing-address-form
        :initial-billing-address="initialBillingAddress"
        :auto-submit="!!initialBillingAddress"
        action-type="updatePaymentMethod"
        @billing-submit-action="handleBillingSubmit"
        @billing-cancel-action="handleBillingCancel"
      />
    </div>

    <!-- step 2 - update payment -->
    <div v-show="step === 'payment'" class="update-payment-address">
      <billing-address-review
        v-if="billingAddress"
        :three-d-secure-billing-address="billingAddress.threeDSecure"
        @billing-back-action="goToBillingStep"
      />

      <form
        ref="form"
        class="standard-form"
        action="/my/account/subscription/update-payment"
        method="post"
        novalidate
        @submit.prevent="handleSubmit"
      >
        <div class="container -flush">
          <callout v-if="paymentInfoError" ref="paymentError" type="error" :closable="false">
            <div>{{ paymentInfoError }}</div>
          </callout>

          <braintree-web-drop-in
            :token="token"
            :total="total"
            payment-flow-type="recurring"
            @payment-drop-in-loaded="onDropInLoad"
            @three-d-secure-customer-canceled="customerCanceled"
          />

          <csrf-input />
          <input
            id="paymentMethodNonce"
            name="paymentMethodNonce"
            type="hidden"
            :value="formData.nonce"
          />
          <input id="paymentType" name="paymentType" type="hidden" :value="formData.paymentType" />

          <!-- billing address - encoded json -->
          <input
            id="billingAddressEncoded"
            name="billingAddressEncoded"
            type="hidden"
            :value="formData.billingAddressEncoded"
          />
          <br />
        </div>
        <div class="form-actions">
          <div class="form-action">
            <submit-button
              type="submit"
              class="btn -main"
              :submitting="processing || !braintreeInstance"
              label="Update Payment"
              :submitting-label="submittingLabel"
              @submit-action="handleSubmit"
            />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import SubmitButton from '../buttons/submit-button.vue'
import CsrfInput from '../forms/csrf-input.vue'
import BillingAddressForm from '../cart/billing-address-form.vue'
import BillingAddressReview from '../cart/billing-address-review.vue'
import Callout from '../notifications/callout.vue'
import BraintreeWebDropIn from '@/components/forms/braintree-web-drop-in.vue'
import { scrollToElemTop } from '@/utils/scroll-to'

/**
 * Update payment form for subscriptions.
 *
 * - step 1: billing address (see billing-address-form.vue)
 * - step 2: payment information (handled here, traditional form submit)
 *
 * On step 1, billing can be "auto submitted"
 * - pass in initial-billing-address (EX: BillingAddress::find())
 * - billing-submit-action event will be emitted as soon as related data and formatting is complete
 *
 * On step 2, user can use any of the following:
 * - just submit a new payment method nonce to associate to subscription
 */
export default defineComponent({
  name: 'SubscriptionUpdatePayment',
  components: {
    SubmitButton,
    CsrfInput,
    BillingAddressForm,
    BillingAddressReview,
    Callout,
    BraintreeWebDropIn,
  },
  props: {
    // should match BillingAddress model (underscore properties)
    initialBillingAddress: {
      type: Object,
      default: () => null,
    },

    // 1 or 12 for monthly or yearly
    billingCycleMonths: {
      type: Number,
      required: true,
    },

    token: {
      type: String,
      required: true,
    },
    total: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // track billing vs payment step
      step: 'billing',

      // step 1 manages this data
      // if step 1 is auto-submitted, then it will be populated as if step 1 was
      // filled out based in initialBillingAddress prop
      billingAddress: null,

      // update payment form data
      formData: {
        nonce: '',
        paymentType: '',
        billingAddressEncoded: '',
      },
      processing: false,
      braintreeInstance: null,
      paymentInfoError: '',
    }
  },

  /* computed */
  computed: {
    ...mapState(useUserAuthStore, ['user']),

    // powers main section header
    // when step changes, he h1 with this property will be focused
    sectionTitle() {
      // step 2 OR auto-submitting step 1 (to avoid title and focus change)
      if (this.step === 'payment' || (this.initialBillingAddress && !this.billingAddress)) {
        return 'Payment Method'
      }

      // step 1
      return 'Billing Address'
    },

    submittingLabel() {
      if (!this.braintreeInstance) {
        return 'Loading...'
      }

      return 'Processing...'
    },
  },

  /* watchers */
  watch: {
    // focus the section header on step change for accessibility
    sectionTitle(newTitle, prevTitle) {
      if (newTitle !== prevTitle) {
        this.$refs.sectionHeader.focus()
      }
    },

    // scroll on payment error
    paymentInfoError(newError, prevError) {
      if (!newError) {
        return
      }

      if (newError === prevError) {
        return
      }

      // must do nextTicket as callout component that contains error is in v-if
      this.$nextTick(() => {
        if (this.$refs && this.$refs.paymentError && this.$refs.paymentError.$el) {
          scrollToElemTop({
            elem: this.$refs.paymentError.$el,
            ms: 250,
          })
        }
      })
    },
  },

  methods: {
    goToBillingStep() {
      this.step = 'billing'
    },

    // cancel on the billing step, default to payment form
    handleBillingCancel() {
      this.step = 'payment'
    },

    // update billing address information from step 1 submit
    // OR step1 auto submit with initialBillingAddress pre-filled
    handleBillingSubmit(billingAddress) {
      this.billingAddress = billingAddress
      this.step = 'payment'
    },

    // handle child event emitter
    onDropInLoad(dropIn) {
      this.braintreeInstance = dropIn
    },

    // the customer canceled the 3D Secure challenge modal
    customerCanceled() {
      this.paymentInfoError =
        'Card authentication canceled. Please try again or select another payment method.'
    },

    async handleSubmit() {
      this.processing = true

      // require billing for any payment method
      if (!this.billingAddress) {
        this.processing = false
        this.goToBillingStep()
        return
      }

      // json encoded billing address
      this.formData.billingAddressEncoded = this.billingAddress.jsonEncoded

      // if no drop in ui
      if (!this.braintreeInstance) {
        this.processing = false
        return
      }

      try {
        // reset braintree specific error
        this.paymentInfoError = ''

        // 3d secure parameters
        // @see https://braintree.github.io/braintree-web-drop-in/docs/current/Dropin.html#requestPaymentMethod
        // @see https://developers.braintreepayments.com/guides/3d-secure/client-side/javascript/v3
        const threeDSecureParameters = {
          amount: this.total,
          email: this.user.email || '',

          // per braintree, for recurring transactions we should request a challenge
          // if it is available
          // @see https://developers.braintreepayments.com/guides/3d-secure/migration/javascript/v3#recurring-transactions
          challengeRequested: true,

          // parse to plain object from observer
          billingAddress: JSON.parse(JSON.stringify(this.billingAddress.threeDSecure)),

          // digital goods delivered electronically
          additionalInformation: {
            productCode: 'DIG',
            deliveryTimeframe: '01',
            deliveryEmail: this.user.email || '',
            recurringFrequency: `${28 * this.billingCycleMonths}`,
          },
        }

        this.braintreeInstance.requestPaymentMethod(
          {
            threeDSecure: threeDSecureParameters,
          },
          (err, payload) => {
            if (err) {
              // No payment method is available.
              // An appropriate error will be shown in the UI.
              console.error(err)

              if (err.code && err.code === 'THREEDS_CARDINAL_SDK_ERROR') {
                // 3d secure specific
                this.paymentInfoError =
                  '3d Secure card authentication failed. Please click "Choose another way to pay" to try again.'
                this.braintreeInstance.clearSelectedPaymentMethod()

                // re-hide any vaulted cards in the UI
                if (this.braintreeInstance.fbCustomizeRecurringUI) {
                  this.braintreeInstance.fbCustomizeRecurringUI()
                }
              } else {
                // default
                this.paymentInfoError = 'Payment information is required.'
              }

              // re-enable form for submits
              this.processing = false
              return
            }

            // 3d secure user cancel handling as it is not handled
            // in the callback above, but on a braintreeInstance event
            if (this.paymentInfoError) {
              this.processing = false
              return
            }

            // Add the nonce to the form and submit
            this.formData.nonce = payload.nonce

            // CreditCard, ApplePayCard, PayPalAccount
            // @see https://braintree.github.io/braintree-web-drop-in/docs/current/Dropin.html#requestPaymentMethod
            this.formData.paymentType = payload.type

            this.$nextTick(() => {
              this.$refs.form.submit()
            })
          },
        )
      } catch (e) {
        // re-enable form for submits
        console.error('err with braintreeInstance.requestPaymentMethod', e)
        this.processing = false
      }
    },
  },
})
</script>
